// Copyright © 2021 The Things Industries B.V.

import React, { useCallback, useState } from 'react'
import { defineMessages } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import AZURE_IOT_HUB_INTEGRATION from '@console/constants/azure-iot-hub-integration.tti'

import Form from '@ttn-lw/components/form'
import Input from '@ttn-lw/components/input'
import SubmitBar from '@ttn-lw/components/submit-bar'
import SubmitButton from '@ttn-lw/components/submit-button'
import ModalButton from '@ttn-lw/components/button/modal-button'
import toast from '@ttn-lw/components/toast'

import RequireRequest from '@ttn-lw/lib/components/require-request'

import Yup from '@ttn-lw/lib/yup'
import sharedMessages from '@ttn-lw/lib/shared-messages'
import { isNotFoundError } from '@ttn-lw/lib/errors/utils'
import attachPromise from '@ttn-lw/lib/store/actions/attach-promise'
import { hostname as hostnameRegexp } from '@ttn-lw/lib/regexp'

import {
  setAppPkgDefaultAssoc,
  getAppPkgDefaultAssoc,
  deleteAppPkgDefaultAssoc,
} from '@console/store/actions/application-packages'

import { selectSelectedApplicationId } from '@console/store/selectors/applications'
import {
  selectApplicationPackageDefaultAssociation,
  selectGetApplicationPackagesError,
} from '@console/store/selectors/application-packages'

const m = defineMessages({
  accessKey: 'Azure IoT Hub access key',
  accessKeyDescription: 'Copy the Azure deployment "iotHubOwnerKey" output',
  disable: 'Disable Azure IoT Hub integration',
  disabled: 'Azure IoT Hub integration disabled',
  disableWarning: 'Are you sure you want to disable the integration?',
  enable: 'Enable Azure IoT Hub integration',
  enabled: 'Azure IoT Hub integration enabled',
  hostName: 'Azure IoT Hub hostname',
  hostNameDescription: 'Copy the Azure deployment "iotHubHostname" output',
  update: 'Update Azure IoT Hub integration',
  updated: 'Azure IoT Hub integration updated',
  validateHostNameFormat: '{field} must be a valid Azure IoT Hostname',
})

const validationSchema = Yup.object()
  .shape({
    data: Yup.object().shape({
      host_name: Yup.string()
        .matches(hostnameRegexp, Yup.passValues(m.validateHostNameFormat))
        .required(sharedMessages.validateRequired),
      access_key: Yup.string().required(sharedMessages.validateRequired),
    }),
  })
  .noUnknown()

const defaultValues = {
  data: {
    host_name: '',
    access_key: '',
  },
}

const promisifiedSetAppPkgDefaultAssoc = attachPromise(setAppPkgDefaultAssoc)
const promisifiedDeleteAppPkgDefaultAssoc = attachPromise(deleteAppPkgDefaultAssoc)

const AzureIoTHubIntegrationForm = () => {
  const [error, setError] = useState(undefined)
  const appId = useSelector(selectSelectedApplicationId)
  const selector = ['data']

  const dispatch = useDispatch()
  const defaultAssociation = useSelector(state =>
    selectApplicationPackageDefaultAssociation(state, AZURE_IOT_HUB_INTEGRATION.DEFAULT_PORT),
  )
  const packageError = useSelector(selectGetApplicationPackagesError)
  const initialValues = validationSchema.cast(defaultAssociation || defaultValues)

  const handleSubmit = useCallback(
    async values => {
      try {
        await dispatch(
          promisifiedSetAppPkgDefaultAssoc(appId, AZURE_IOT_HUB_INTEGRATION.DEFAULT_PORT, {
            package_name: AZURE_IOT_HUB_INTEGRATION.DEFAULT_PACKAGE_NAME,
            ...values,
          }),
        )
        toast({
          message: Boolean(defaultAssociation) ? m.updated : m.enabled,
          type: toast.types.SUCCESS,
        })
      } catch (error) {
        setError(error)
      }
    },
    [appId, dispatch, defaultAssociation],
  )

  const handleDisable = useCallback(async () => {
    try {
      await dispatch(
        promisifiedDeleteAppPkgDefaultAssoc(appId, AZURE_IOT_HUB_INTEGRATION.DEFAULT_PORT, {
          package_name: AZURE_IOT_HUB_INTEGRATION.DEFAULT_PACKAGE_NAME,
        }),
      )
      toast({
        message: m.disabled,
        type: toast.types.SUCCESS,
      })
    } catch (error) {
      setError(error)
    }
  }, [appId, dispatch])

  if (packageError && !isNotFoundError(packageError)) {
    throw packageError
  }

  return (
    <RequireRequest
      requestAction={getAppPkgDefaultAssoc(appId, AZURE_IOT_HUB_INTEGRATION.DEFAULT_PORT, selector)}
    >
      <Form
        error={error}
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <Form.Field
          name="data.host_name"
          title={m.hostName}
          component={Input}
          description={m.hostNameDescription}
          required
        />
        <Form.Field
          name="data.access_key"
          title={m.accessKey}
          component={Input}
          description={m.accessKeyDescription}
          sensitive
          required
        />
        <SubmitBar>
          <Form.Submit
            component={SubmitButton}
            message={Boolean(defaultAssociation) ? m.update : m.enable}
          />
          {Boolean(defaultAssociation) && (
            <ModalButton
              type="button"
              message={m.disable}
              modalData={{
                message: m.disableWarning,
              }}
              onApprove={handleDisable}
              danger
              naked
            />
          )}
        </SubmitBar>
      </Form>
    </RequireRequest>
  )
}

export default AzureIoTHubIntegrationForm
